import { ChevronRight } from 'lucide-react';
import { cn } from '../../lib/cn.ts';
import { Avatar, AvatarFallback } from '../avatar.tsx';
import { LanguagePill } from '../pills/language-pill.tsx';
import { type PillTone, StatusPill } from '../pills/status-pill.tsx';

export interface RecentCallItem {
  id: string;
  name: string;
  meta?: string;
  lang?: string;
  statusLabel?: string;
  statusTone?: PillTone;
  duration?: string;
}

export interface RecentCallsListProps {
  rows: RecentCallItem[];
  onSelect?: (id: string) => void;
  className?: string;
  'data-testid'?: string;
}

function initials(name: string) {
  return name
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/**
 * RecentCallsList — a hospitality ledger of recent conversations. Rows are
 * keyboard-operable (Enter/Space) and carry a guest, language, status and
 * duration.
 */
export function RecentCallsList({
  rows,
  onSelect,
  className,
  'data-testid': testId,
}: RecentCallsListProps) {
  return (
    <ul data-testid={testId} className={cn('flex flex-col', className)}>
      {rows.map((row) => (
        <li key={row.id}>
          <button
            type="button"
            data-testid={`recent-call-row-${row.id}`}
            onClick={() => onSelect?.(row.id)}
            className={cn(
              'flex w-full cursor-pointer items-center gap-4 rounded-md border-b border-[var(--color-border)] px-2 py-3.5 text-left last:border-0',
              'transition-colors hover:bg-[var(--color-line-2)]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]',
            )}
          >
            <Avatar className="h-9 w-9 shrink-0">
              <AvatarFallback>{initials(row.name)}</AvatarFallback>
            </Avatar>
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-medium text-[var(--color-charcoal)]">
                {row.name}
              </span>
              {row.meta && (
                <span className="truncate font-[var(--font-mono)] text-[11px] text-[var(--color-stone-mid)]">
                  {row.meta}
                </span>
              )}
            </div>
            {row.lang && <LanguagePill code={row.lang} />}
            {row.statusLabel && <StatusPill label={row.statusLabel} tone={row.statusTone} />}
            {row.duration && (
              <span className="font-[var(--font-mono)] text-xs text-[var(--color-stone-mid)]">
                {row.duration}
              </span>
            )}
            <ChevronRight
              aria-hidden="true"
              className="size-4 shrink-0 text-[var(--color-stone-300)]"
            />
          </button>
        </li>
      ))}
    </ul>
  );
}
